import { SparklesIcon, Loader2Icon, ZapIcon } from 'lucide-react';
// import { GitBranchIcon } from 'lucide-react'; // For custom review - currently disabled
import { memo, Suspense, useState, useEffect } from 'react';

import { ReviewAvatar } from './review-avatar';
import { Checkbox } from './ui/checkbox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from './ui/command';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Skeleton } from './ui/skeleton';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { MesaReviewState } from '@/features/pulls/services/pulls';
import { TERMINAL_REVIEW_STATES, useReviewers } from '@/hooks/use-reviewers';

type PotentialReviewer = {
  login: string;
  avatarUrl: string;
  requested: boolean;
  lastReviewState?: MesaReviewState;
  isBot: boolean;
};

type ReviewType = 'full' | 'incremental' | 'range';

interface ReviewersAndReviewTriggerProps {
  onTriggerReview?: (params: {
    reviewType: ReviewType;
    fromCommit?: string;
    toCommit?: string;
    customPrompt?: string;
  }) => void;
  isReviewLoading?: boolean;
  canTriggerReview?: boolean;
}

const Login = memo(({ login }: { login: string }) => {
  return (
    <div className="flex items-center gap-1">
      <span className="text-sm">{login.replace(/\[bot\]/, '')}</span>
      {login.endsWith('[bot]') && (
        <div className="text-xs px-2 text-muted-foreground border border-stone-300 rounded-full">Bot</div>
      )}
    </div>
  );
});

function isTerminalReviewState(state: MesaReviewState | undefined): boolean {
  return !!(state && TERMINAL_REVIEW_STATES.includes(state));
}

function ReviewersAndReviewTriggerInner({
  onTriggerReview,
  isReviewLoading = false,
  canTriggerReview = false,
}: ReviewersAndReviewTriggerProps) {
  const { potentialReviewers, toggleReviewer, hasNewCommitsSinceLastMesaReview } = useReviewers();
  const [showAdvancedDialog, setShowAdvancedDialog] = useState(false);
  const [reviewType, setReviewType] = useState<ReviewType>('full');
  const [fromCommit, setFromCommit] = useState('');
  const [toCommit, setToCommit] = useState('');
  const [customPrompt, setCustomPrompt] = useState('');
  const [loadingReviewType, setLoadingReviewType] = useState<ReviewType | null>(null);

  const sorted = potentialReviewers.sort((a, b) => a.login.localeCompare(b.login));

  const { reviewers, requestedReviewers, rest } = (() => {
    const reviewers: PotentialReviewer[] = [];
    const requestedReviewers: PotentialReviewer[] = [];
    const rest: PotentialReviewer[] = [];

    sorted.forEach((c) => {
      if (
        c.lastReviewState &&
        ['AGENT_REVIEWING', 'APPROVED', 'CHANGES_REQUESTED', 'COMMENTED'].includes(c.lastReviewState)
      ) {
        reviewers.push(c);
      } else if (c.requested) {
        requestedReviewers.push(c);
      } else {
        rest.push(c);
      }
    });

    return { reviewers, requestedReviewers, rest };
  })();

  const handleQuickReview = (type: ReviewType) => {
    if (type === 'range') {
      setReviewType('range');
      setShowAdvancedDialog(true);
    } else {
      setLoadingReviewType(type);
      onTriggerReview?.({ reviewType: type });
    }
  };

  // Clear loading state when isReviewLoading changes from true to false
  useEffect(() => {
    if (!isReviewLoading) {
      // Small delay to ensure the state has settled
      const timer = setTimeout(() => {
        setLoadingReviewType(null);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isReviewLoading]);

  const handleAdvancedReview = () => {
    const params: Parameters<NonNullable<typeof onTriggerReview>>[0] = {
      reviewType,
      customPrompt: customPrompt.trim() || undefined,
    };

    if (reviewType === 'range') {
      params.fromCommit = fromCommit;
      params.toCommit = toCommit;
    }

    setLoadingReviewType(reviewType);
    onTriggerReview?.(params);
    setShowAdvancedDialog(false);
    resetForm();
  };

  const resetForm = () => {
    setReviewType('full');
    setFromCommit('');
    setToCommit('');
    setCustomPrompt('');
  };

  const isFormValid = reviewType !== 'range' || (fromCommit && toCommit);

  return (
    <>
      <Popover>
        <PopoverTrigger asChild>
          <div className="flex items-center gap-2 h-full hover:bg-stone-200 hover:text-accent-foreground rounded-full py-2 px-2.5 border border-transparent hover:border-stone-300 transition-all cursor-pointer">
            <div className="flex items-center gap-1.5">
              {isReviewLoading ? (
                <Loader2Icon className="size-3.5 text-purple-600 animate-spin" />
              ) : (
                <SparklesIcon className="size-3.5 text-purple-600" />
              )}
              <span className="text-xs font-medium">Reviewers</span>
            </div>
            <div className="flex items-center">
              {reviewers.length + requestedReviewers.length > 0 ? (
                <>
                  {[...reviewers, ...requestedReviewers].slice(0, 3).map((reviewer) => (
                    <ReviewAvatar
                      key={reviewer.login}
                      avatarUrl={reviewer.avatarUrl}
                      reviewState={reviewer.lastReviewState}
                      fallbackText={reviewer.login}
                    />
                  ))}
                  {reviewers.length + requestedReviewers.length > 3 && (
                    <div className="size-6 flex items-center justify-center">
                      <span className="text-xs text-muted-foreground">
                        +{reviewers.length + requestedReviewers.length - 3}
                      </span>
                    </div>
                  )}
                </>
              ) : (
                <div></div>
              )}
            </div>
          </div>
        </PopoverTrigger>
        <PopoverContent className="w-[300px] p-0" side="bottom" align="start">
          <Command>
            <CommandInput placeholder="Search for actions or reviewers..." />
            <CommandList>
              <CommandEmpty>No results found.</CommandEmpty>

              {/* Review Actions Section */}
              {canTriggerReview && onTriggerReview && (
                <>
                  <CommandGroup heading="Mesa Reviewer">
                    <CommandItem
                      onSelect={() => handleQuickReview('full')}
                      disabled={isReviewLoading}
                      className="flex items-center gap-2 w-full"
                    >
                      {loadingReviewType === 'full' ? (
                        <Loader2Icon className="h-4 w-4 animate-spin" />
                      ) : (
                        <SparklesIcon className="h-4 w-4" />
                      )}
                      <div className="flex-1">
                        <div className="font-medium">Request Full Review</div>
                        <div className="text-xs text-muted-foreground">Mesa reviews all changes in the PR</div>
                      </div>
                    </CommandItem>

                    {hasNewCommitsSinceLastMesaReview && (
                      <CommandItem
                        onSelect={() => handleQuickReview('incremental')}
                        disabled={isReviewLoading}
                        className="flex items-center gap-2 w-full"
                      >
                        {loadingReviewType === 'incremental' ? (
                          <Loader2Icon className="h-4 w-4 animate-spin" />
                        ) : (
                          <ZapIcon className="h-4 w-4" />
                        )}
                        <div className="flex-1">
                          <div className="font-medium">Request Incremental Review</div>
                          <div className="text-xs text-muted-foreground">Mesa reviews only new commits</div>
                        </div>
                      </CommandItem>
                    )}

                    {/* <CommandItem
                      onSelect={() => handleQuickReview('range')}
                      disabled={isReviewLoading}
                      className="flex items-center gap-2 w-full"
                    >
                      <GitBranchIcon className="h-4 w-4" />
                      <div className="flex-1">
                        <div className="font-medium">Request Custom Review...</div>
                        <div className="text-xs text-muted-foreground">Mesa reviews specific commits</div>
                      </div>
                    </CommandItem> */}
                  </CommandGroup>

                  <CommandSeparator className="my-2" />
                </>
              )}

              {/* Reviewers Section */}
              <CommandGroup heading="Add Reviewers">
                {[...reviewers, ...requestedReviewers, ...rest].map((reviewer) => {
                  if (reviewer.isBot && !isTerminalReviewState(reviewer.lastReviewState)) {
                    return null;
                  }

                  return (
                    <CommandItem
                      key={reviewer.login}
                      onSelect={() => toggleReviewer(reviewer.login, !reviewer.requested)}
                      className="flex items-center gap-2 w-full"
                    >
                      <Checkbox
                        checked={reviewer.requested || isTerminalReviewState(reviewer.lastReviewState)}
                        disabled={isTerminalReviewState(reviewer.lastReviewState)}
                      />
                      <ReviewAvatar
                        avatarUrl={reviewer.avatarUrl}
                        reviewState={reviewer.lastReviewState}
                        fallbackText={reviewer.login}
                      />
                      <Login login={reviewer.login} />
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Advanced Review Dialog */}
      <Dialog open={showAdvancedDialog} onOpenChange={setShowAdvancedDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Advanced Review Options</DialogTitle>
            <DialogDescription>
              Configure your review preferences and optionally add custom instructions.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Review Type</Label>
              <Select value={reviewType} onValueChange={(v) => setReviewType(v as ReviewType)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="full">Full Review - All changes</SelectItem>
                  <SelectItem value="incremental">Incremental - Since last Mesa review</SelectItem>
                  <SelectItem value="range">Custom Range - Specific commits</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {reviewType === 'range' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="fromCommit">From Commit (SHA)</Label>
                  <input
                    id="fromCommit"
                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    placeholder="e.g., abc1234"
                    value={fromCommit}
                    onChange={(e) => setFromCommit(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="toCommit">To Commit (SHA)</Label>
                  <input
                    id="toCommit"
                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    placeholder="e.g., def5678"
                    value={toCommit}
                    onChange={(e) => setToCommit(e.target.value)}
                  />
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label htmlFor="customPrompt">Custom Instructions (Optional)</Label>
              <Textarea
                id="customPrompt"
                placeholder="e.g., Focus on security implications, check for performance issues..."
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value)}
                rows={4}
              />
              <p className="text-xs text-muted-foreground">
                Add specific instructions for the AI reviewer to focus on during this review.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdvancedDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAdvancedReview} disabled={!isFormValid || isReviewLoading}>
              {isReviewLoading ? 'Generating...' : 'Start Review'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function ReviewersAndReviewTrigger(props: ReviewersAndReviewTriggerProps) {
  return (
    <Suspense
      fallback={
        <div className="flex items-center px-2">
          <Skeleton className="w-24 h-8 rounded-full overflow-hidden" />
        </div>
      }
    >
      <ReviewersAndReviewTriggerInner {...props} />
    </Suspense>
  );
}
